import type { TutorialStatus } from '@/tours/types';

/**
 * Persistence for tutorial state — localStorage, by design (decided PY-309:
 * no backend persistence). Completed/dismissed flags are therefore
 * per-browser: a new device or cleared storage re-offers the tours, and
 * "Reset tutorials" applies to the current browser. The async signatures are
 * kept so a server-side swap would stay contained to this file if that
 * decision ever changes.
 */

export interface TutorialState {
  tutorialKey: string;
  status: TutorialStatus;
}

const STORAGE_KEY = 'gaia:tutorial-states';

function readAll(): Record<string, TutorialStatus> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, TutorialStatus>) : {};
  } catch {
    return {};
  }
}

function writeAll(states: Record<string, TutorialStatus>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(states));
  } catch {
    // Storage unavailable (private mode) — state just won't survive reloads.
  }
}

export function fetchTutorialStates(): Promise<TutorialState[]> {
  return Promise.resolve(
    Object.entries(readAll()).map(([tutorialKey, status]) => ({ tutorialKey, status }))
  );
}

export function saveTutorialState(tutorialKey: string, status: TutorialStatus): Promise<void> {
  writeAll({ ...readAll(), [tutorialKey]: status });
  return Promise.resolve();
}

export function resetTutorialStates(): Promise<void> {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to reset.
  }
  return Promise.resolve();
}
